import React, { useMemo, useState } from 'react';
import "../assets/style/deposit.scss";
import { Form, Spinner, Image } from "react-bootstrap"
import { Dai, Usdt, Usdc, Ethereum, Btc } from 'react-web3-icons';
import toIcn from "../assets/images/logo.svg"
import { IoMdWallet } from "react-icons/io"
import { FaEthereum } from "react-icons/fa"
import { useAccount, useConnect, useNetwork, useSwitchNetwork, useBalance } from 'wagmi'
import { InjectedConnector } from 'wagmi/connectors/injected'
import TabMenu from './TabMenu';
import { HiSwitchHorizontal } from "react-icons/hi"
import metamask from "../assets/images/metamask.svg"
import { depositETH, depositERC20 } from '../utils/bridge';
import { createL1PublicClient } from '../utils/viemClient';

// 辅助函数：清理环境变量值（去除注释和空格）
const cleanEnvValue = (value) => {
    if (!value) return value;
    // 去除注释（# 及其后面的内容）
    const cleaned = String(value).split('#')[0].trim();
    return cleaned;
}

const Deposit = () => {
    // 从环境变量获取链ID，确保是数字类型
    const L1_CHAIN_ID = Number(cleanEnvValue(process.env.REACT_APP_L1_CHAIN_ID)) || 11155111; // 默认 Sepolia
    const L2_CHAIN_ID = Number(cleanEnvValue(process.env.REACT_APP_L2_CHAIN_ID));

    const [ethValue, setEthValue] = useState("")
    const [sendToken, setSendToken] = useState("ETH")
    const { address, isConnected } = useAccount()
    const [errorInput, setErrorInput] = useState("")
    const [loader, setLoader] = useState(false)
    const { chain, chains } = useNetwork()
    const [checkMetaMask, setCheckMetaMask] = useState("");
    const l1PublicClient = useMemo(() => {
        try {
            return createL1PublicClient()
        } catch (error) {
            console.error('初始化 L1 Public Client 失败:', error)
            return null
        }
    }, [])
    const tokenConfig = useMemo(() => ({
        ETH: { decimals: 18 },
        DAI: {
            l1: process.env.REACT_APP_L1_DAI,
            l2: process.env.REACT_APP_L2_DAI,
            decimals: 18
        },
        USDC: {
            l1: process.env.REACT_APP_L1_USDC,
            l2: process.env.REACT_APP_L2_USDC,
            decimals: 6
        },
        USDT: {
            l1: process.env.REACT_APP_L1_USDT,
            l2: process.env.REACT_APP_L2_USDT,
            decimals: 6
        },
        wBTC: {
            l1: process.env.REACT_APP_L1_wBTC,
            l2: process.env.REACT_APP_L2_wBTC,
            decimals: 8
        }
    }), [])

    const { connect } = useConnect({
        connector: new InjectedConnector({ chains }), onError(error) {
            console.log('Error', error)
        },
        onMutate(args) {
            console.log('Mutate', args)
            if (args.connector.ready === true) {
                setCheckMetaMask(false)
            } else {
                setCheckMetaMask(true)
            }
        },
        onSettled(data, error) {
            console.log('Settled', { data, error })
        },
        onSuccess(data) {
            console.log('Success', data)
        },
    })
    const { switchNetwork } = useSwitchNetwork({
        throwForSwitchChainNotSupported: true,
        onError(error) {
            console.log('Error', error)
        },
        onMutate(args) {
            console.log('Mutate', args)
        },
        onSettled(data, error) {
            console.log('Settled', { data, error })
        },
        onSuccess(data) {
            console.log('Success', data)
        },
    })


    const { data } = useBalance({ address: address, watch: true, chainId: L1_CHAIN_ID })

    // 添加 enabled 选项，只有在地址和代币地址都有效时才查询，避免无效合约地址错误
    const dataUSDT = useBalance({
        address: address,
        token: process.env.REACT_APP_L1_USDT,
        watch: true,
        chainId: L1_CHAIN_ID,
        enabled: !!address && !!process.env.REACT_APP_L1_USDT,
    })
    const dataDAI = useBalance({
        address: address,
        token: process.env.REACT_APP_L1_DAI,
        watch: true,
        chainId: L1_CHAIN_ID,
        enabled: !!address && !!process.env.REACT_APP_L1_DAI,
    })
    const dataUSDC = useBalance({
        address: address,
        token: process.env.REACT_APP_L1_USDC,
        watch: true,
        chainId: L1_CHAIN_ID,
        enabled: !!address && !!process.env.REACT_APP_L1_USDC,
    })
    const datawBTC = useBalance({
        address: address,
        token: process.env.REACT_APP_L1_wBTC,
        watch: true,
        chainId: L1_CHAIN_ID,
        enabled: !!address && !!process.env.REACT_APP_L1_wBTC,
    })

    const handleSwitch = () => {
        if (!L1_CHAIN_ID || isNaN(L1_CHAIN_ID)) {
            console.error('L1_CHAIN_ID is not valid:', process.env.REACT_APP_L1_CHAIN_ID);
            return;
        }
        switchNetwork(L1_CHAIN_ID)
    }


    const handleDeposit = async () => {
        if (!address) {
            setErrorInput("Please connect your wallet first.")
            return
        }
        if (!ethValue) {
            setErrorInput("Please enter the amount")
            return
        }
        if (!(parseFloat(ethValue) > 0)) {
            setErrorInput("Invalid Amount Entered!")
            return
        }

        const tokenMeta = tokenConfig[sendToken] || tokenConfig.ETH
        if (sendToken !== 'ETH' && (!tokenMeta?.l1 || !tokenMeta?.l2)) {
            setErrorInput(`${sendToken} token addresses are not configured`)
            return
        }

        setLoader(true)
        setErrorInput("")

        try {
            let hash
            if (sendToken === "ETH") {
                hash = await depositETH(ethValue, address)
            } else {
                hash = await depositERC20(
                    tokenMeta.l1,
                    tokenMeta.l2,
                    ethValue,
                    tokenMeta.decimals,
                    address
                )
            }

            if (l1PublicClient && hash) {
                await l1PublicClient.waitForTransactionReceipt({ hash })
            }

            setEthValue("")
        } catch (error) {
            console.error(error)
            setErrorInput(error?.shortMessage || error?.message || "Deposit failed")
        } finally {
            setLoader(false)
        }
    }
    const [checkDisabled, setCheckDisabled] = useState(false)
    const handleChange = (e) => {
        if (sendToken === 'ETH') {
            if (Number(data?.formatted) < e.target.value) {
                setErrorInput("Insufficient ETH balance.")
                setCheckDisabled(true)
            } else {
                setCheckDisabled(false)
                setErrorInput("")
            }
            setEthValue(e.target.value)
        }
        if (sendToken === 'DAI') {
            if (Number(dataDAI.data?.formatted) < e.target.value) {
                setErrorInput("Insufficient DAI balance.")
                setCheckDisabled(true)
            } else {
                setCheckDisabled(false)
                setErrorInput("")
            }
            setEthValue(e.target.value)
        }
        if (sendToken === 'USDT') {
            if (Number(dataUSDT.data?.formatted) < e.target.value) {
                setErrorInput("Insufficient USDT balance.")
                setCheckDisabled(true)
            } else {
                setCheckDisabled(false)
                setErrorInput("")
            }
            setEthValue(e.target.value)
        }
        if (sendToken === 'wBTC') {
            if (Number(datawBTC.data?.formatted) < e.target.value) {
                setErrorInput("Insufficient wBTC balance.")
                setCheckDisabled(true)
            } else {
                setCheckDisabled(false)
                setErrorInput("")
            }
            setEthValue(e.target.value)
        }
        if (sendToken === 'USDC') {
            if (Number(dataUSDC.data?.formatted) < e.target.value) {
                setErrorInput("Insufficient USDC balance.")
                setCheckDisabled(true)
            } else {
                setErrorInput("")
                setCheckDisabled(false)
            }
            setEthValue(e.target.value)
        }

    }

    return (
        <>
            <div className='bridge_wrap'>
                <TabMenu />
                <section className='deposit_wrap'>
                    <div className='deposit_price_wrap'>
                        <div className='deposit_price_title'>
                            <p>From</p>
                            <h5><FaEthereum /> Sepolia Testnet</h5>
                        </div>
                        <div className='deposit_input_wrap'>
                            <Form>
                                <div className='deposit_inner_input'>
                                    <Form.Control type='number' value={ethValue} onChange={handleChange} placeholder="0" min="0" step="any" />
                                    <Form.Select
                                        aria-label="Default select example"
                                        className='select_wrap'
                                        value="ETH"
                                        disabled
                                    >
                                        <option value="ETH">ETH</option>
	                            </Form.Select>
                                </div>
                                <div className='input_icn_wrap'>
                                    {sendToken === "ETH" ? <span className='input_icn'><Ethereum style={{ fontSize: '1.5rem' }} /></span> : sendToken === "DAI" ? <span className='input_icn'><Dai style={{ fontSize: '1.5rem' }} /></span> : sendToken === "USDT" ? <span className='input_icn'><Usdt style={{ fontSize: '1.5rem' }} /></span> : sendToken === "wBTC" ? <span className='input_icn'><Btc style={{ fontSize: '1.5rem' }} /></span> : <span className='input_icn'><Usdc style={{ fontSize: '1.5rem' }} /></span>}
                                </div>
                            </Form>
                        </div>
                        {errorInput && <small className='text-danger'>{errorInput}</small>}
                        {sendToken === 'ETH' ? address && <p className='wallet_bal mt-2'>Balance: {Number(data?.formatted).toFixed(5)} ETH</p> : sendToken === 'USDT' ? address && <p className='wallet_bal mt-2'>Balance: {Number(dataUSDT.data?.formatted).toFixed(5)} USDT</p> : sendToken === 'DAI' ? address && <p className='wallet_bal mt-2'>Balance: {Number(dataDAI.data?.formatted).toFixed(5)} DAI</p> : sendToken === 'wBTC' ? address && <p className='wallet_bal mt-2'>Balance: {Number(datawBTC.data?.formatted).toFixed(5)} wBTC</p> : address && <p className='wallet_bal mt-2'>Balance: {Number(dataUSDC.data?.formatted).toFixed(5)} USDC</p>}

                    </div>
                    <div className='deposit_details_wrap'>
                        <div className="deposit_details">
                            <p>To</p>
                            <h5><Image src={toIcn} alt="To icn" fluid /> {process.env.REACT_APP_L2_NETWORK_NAME || "L2"}</h5>
                        </div>
                        <div className='deposit_inner_details'>
                            {sendToken === "ETH" ? <span className='input_icn'> <Ethereum style={{ fontSize: '1.5rem' }} /></span> : sendToken === "DAI" ? <span className='input_icn'><Dai style={{ fontSize: '1.5rem' }} /></span> : sendToken === "USDT" ? <span className='input_icn'> <Usdt style={{ fontSize: '1.5rem' }} /></span> : sendToken === "wBTC" ? <span className='input_icn'> <Btc style={{ fontSize: '1.5rem' }} /></span> : <span className='input_icn'> <Usdc style={{ fontSize: '1.5rem' }} /></span>}  <p> You'll receive: {ethValue ? ethValue : "0"} {sendToken}</p>
                        </div>
                    </div>
                    <div className="deposit_btn_wrap">
                        {checkMetaMask === true ? <a className='btn deposit_btn' href='https://metamask.io/' target='_blank' rel='noreferrer'><Image src={metamask} alt="metamask icn" fluid /> Please Install Metamask Wallet</a> : !isConnected ? <button className='btn deposit_btn' onClick={() => connect()}><IoMdWallet />Connect Wallet</button> : chain.id !== L1_CHAIN_ID ? <button className='btn deposit_btn' onClick={handleSwitch}><HiSwitchHorizontal />Switch to Sepolia</button> :
                            checkDisabled ? <button className='btn deposit_btn' disabled={true}>Deposit</button> :
                                <button className='btn deposit_btn' onClick={handleDeposit} disabled={loader ? true : false}> {loader ? <Spinner animation="border" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </Spinner> : "Deposit"} </button>}
                    </div>
                </section>
            </div>
        </>
    )
}

export default Deposit
